import {Inject, Injectable, Optional} from '@angular/core';
import { NgModule } from '@angular/core';
import { DateAdapter, MAT_DATE_FORMATS, MatNativeDateModule } from '@angular/material/core';
import {MatDatepickerModule} from '@angular/material/datepicker';
import {MomentDateAdapter} from '@angular/material-moment-adapter';
import {MAT_DATE_LOCALE} from '@angular/material/core';
import moment, {Moment} from 'moment';

@Injectable()
export class CustomDateAdapter extends MomentDateAdapter {

    allowedString: string | null = null; // Allowed string based on condition
    text: string | null = null; // Allowed string based on condition

    constructor(@Optional() @Inject(MAT_DATE_LOCALE) matDateLocale: string) {
        super(matDateLocale);
    }

    // Setter to update the allowed string dynamically
    setAllowedString(value: string) {
        this.allowedString = value;
    }

    override parse(value: any, parseFormat?: string): Moment | null {
        this.text = value;
        if (typeof value === 'string') {
            // Allow the specific string and return a valid placeholder Moment object
            if (this.allowedString && value === this.allowedString) {
                return moment('9999-12-31', 'YYYY-MM-DD'); // Placeholder date for valid entry
            }

            // Clean input by removing all non-numeric characters except the '/'
            const onlyNumbersDateFormat = value.replace(/[^0-9\/]/g, '');

            // Handle MM/DD/YYYY or M/D/YYYY (single digit month and day)
            if (/^\d{1,2}\/\d{1,2}\/\d{4}$/.test(onlyNumbersDateFormat)) {
                // Split the input by '/' and pad the month and day to 2 digits
                const parts = onlyNumbersDateFormat.split('/');
                const month = parts[0].padStart(2, '0');  // Ensure month is 2 digits
                const day = parts[1].padStart(2, '0');    // Ensure day is 2 digits
                const year = parts[2];

                return moment(`${month}/${day}/${year}`, 'MM/DD/YYYY', true);
            }

            // Handle cases with 8 digits (e.g., 10102002 -> MMDDYYYY format)
            if (/^\d{8}$/.test(onlyNumbersDateFormat)) {
                const parsedMomentFormat = moment(onlyNumbersDateFormat, 'MMDDYYYY', true);
                return parsedMomentFormat.isValid() ? parsedMomentFormat : moment.invalid();
            } else {
                return moment.invalid();
            }
        } else {
            return super.parse(value, parseFormat);
        }
    }

    override format(date: Moment, displayFormat: Object): string {
        // If the value is the placeholder '99/99/9999', return that string
        if (this.allowedString && date.isValid() && (date.format('YYYY-MM-DD') === '9999-12-31')) {
            return this.allowedString;  // Return the placeholder string as is
        } else if (date.format('YYYY-MM-DD') === '1970-01-01') { // 1970-01-01 default date
            return this.text || '';
        } else {
            // Always format output as MM/DD/YYYY
            return date.format('MM/DD/YYYY');
        }
    }
}

export const MY_DATE_FORMATS = {
    parse: {
        dateInput: 'MM/DD/YYYY',
    },
    display: {
        dateInput: 'MM/DD/YYYY',
        monthYearLabel: 'MMM YYYY',
        dateA11yLabel: 'LL',
        monthYearA11yLabel: 'MMMM YYYY',
    },
};

@NgModule({
    imports: [MatDatepickerModule, MatNativeDateModule],
    providers: [
        {
            provide: DateAdapter, useClass: CustomDateAdapter
        },
        {
            provide: MAT_DATE_FORMATS, useValue: MY_DATE_FORMATS
        }
    ]
})
export class DatePickerModule {}
